import { AnyObject } from '@data-driven-forms/react-form-renderer/dist/cjs/common';

import { FIELD_NAMES, GENDER_LABELS, StepNames } from '../..';
import {
  DBFormSchema,
  fieldDownloadFileSection,
  fieldFormattedPlainTextSection,
  fieldPlainTextSection,
  headerSection,
  plainTextSection,
  spacerSection
} from '../field-helpers';

const { patientFields, prescriberFields, organizationFields, claimInformationFields } = FIELD_NAMES;

export const confirmTabFields = (schema: DBFormSchema, nextStep?: StepNames | AnyObject) => {
  const confirmTab = schema?.confirm_tab;

  if (!confirmTab || confirmTab?.hideTab) return null;

  return {
    title: confirmTab?.label ?? 'Confirm',
    name: StepNames.CONFIRM,
    nextStep: confirmTab?.nextStep ?? nextStep,
    submitStep: true,
    fields: [
      headerSection(schema?.personal_information_header?.label, schema?.personal_information_header),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3 spacer-hr' }),
      fieldPlainTextSection(patientFields.firstName, schema?.[patientFields.firstName], {
        sizes: { md: 3 },
        style: { fontWeight: '700' }
      }),
      fieldPlainTextSection(patientFields.middleName, schema?.[patientFields.middleName], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(patientFields.lastName, schema?.[patientFields.lastName], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(patientFields.dob, schema?.[patientFields.dob], {
        sizes: { md: 3 }
      }),
      spacerSection('patient_1', { sizes: { sm: 12 } }),
      fieldFormattedPlainTextSection(patientFields.gender, schema?.[patientFields.gender], {
        sizes: { md: 4 },
        formatter: (val: string) => GENDER_LABELS[val] || val
      }),
      fieldPlainTextSection(patientFields.genderOther, schema?.[patientFields.genderOther], {
        sizes: { md: 4 },
        condition: {
          when: patientFields.gender,
          is: 'O'
        }
      }),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3' }),

      headerSection(schema?.provider_information_header?.label, schema?.provider_information_header),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3 spacer-hr' }),
      fieldPlainTextSection(prescriberFields.firstName, schema?.[prescriberFields.firstName], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(prescriberFields.middleName, schema?.[prescriberFields.middleName], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(prescriberFields.lastName, schema?.[prescriberFields.lastName], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(prescriberFields.taxId, schema?.[prescriberFields.taxId], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(prescriberFields.stateLicenseNumber, schema?.[prescriberFields.stateLicenseNumber], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(prescriberFields.licensingState, schema?.[prescriberFields.licensingState], {
        sizes: { md: 3 }
      }),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3' }),

      headerSection(schema?.practice_information_header?.label, schema?.practice_information_header),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3 spacer-hr' }),
      fieldPlainTextSection(organizationFields.name, schema?.[organizationFields.name], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(organizationFields.npi, schema?.[organizationFields.npi], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(organizationFields.taxId, schema?.[organizationFields.taxId], {
        sizes: { md: 3 }
      }),
      headerSection(schema?.practice_location_information_header?.label, schema?.practice_location_information_header),
      //spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3' }),
      fieldPlainTextSection(prescriberFields.locationAddressFull, schema?.[prescriberFields.locationAddressFull], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(prescriberFields.locationPhone1, schema?.[prescriberFields.locationPhone1], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(prescriberFields.locationFaxNumber, schema?.[prescriberFields.locationFaxNumber], {
        sizes: { md: 3 }
      }),

      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3' }),
      headerSection(
        claimInformationFields.copayClaimInformationHeader,
        schema?.[claimInformationFields.copayClaimInformationHeader]
      ),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3 spacer-hr' }),
      spacerSection('claim_information_1', {
        sizes: { sm: 12 }
      }),
      fieldPlainTextSection(
        claimInformationFields.paymentMethod,
        schema?.[claimInformationFields.preferredPaymentMethod],
        {
          sizes: { md: 3 }
        }
      ),
      fieldPlainTextSection(claimInformationFields.emailId, schema?.[claimInformationFields.emailId], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(claimInformationFields.claimType, schema?.[claimInformationFields.claimType], {
        sizes: { md: 3 }
      }),
      fieldPlainTextSection(claimInformationFields.claimAmount, schema?.[claimInformationFields.RequestedclaimAmount], {
        sizes: { md: 3 }
      }),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3' }),

      headerSection(claimInformationFields.payeeHeader, schema?.[claimInformationFields.payeeHeader]),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3 spacer-hr' }),
      fieldPlainTextSection(claimInformationFields.payeeType, schema?.[claimInformationFields.payeeType], {
        sizes: { md: 3 }
      }),

      fieldPlainTextSection(
        claimInformationFields.patient_first_name,
        schema?.[claimInformationFields.patient_first_name],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Patient'
          }
        }
      ),
      fieldPlainTextSection(
        claimInformationFields.patient_middle_name,
        schema?.[claimInformationFields.patient_middle_name],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Patient'
          }
        }
      ),
      fieldPlainTextSection(
        claimInformationFields.patient_last_name,
        schema?.[claimInformationFields.patient_last_name],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Patient'
          }
        }
      ),
      fieldPlainTextSection(
        claimInformationFields.patient_address_1,
        schema?.[claimInformationFields.patient_address_1],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Patient'
          }
        }
      ),
      fieldPlainTextSection(
        claimInformationFields.patient_address_2,
        schema?.[claimInformationFields.patient_address_2],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Patient'
          }
        }
      ),
      fieldPlainTextSection(patientFields.city, schema?.[patientFields.city], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      fieldPlainTextSection(claimInformationFields.patient_state, schema?.[claimInformationFields.patient_state], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      fieldPlainTextSection(claimInformationFields.patient_zip, schema?.[claimInformationFields.patient_zip], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),

      fieldPlainTextSection(
        claimInformationFields.prescriber_first_name,
        schema?.[claimInformationFields.prescriber_first_name],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Provider'
          }
        }
      ),
      fieldPlainTextSection(
        claimInformationFields.prescriber_middle_name,
        schema?.[claimInformationFields.prescriber_middle_name],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Provider'
          }
        }
      ),
      fieldPlainTextSection(
        claimInformationFields.prescriber_last_name,
        schema?.[claimInformationFields.prescriber_last_name],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Provider'
          }
        }
      ),
      fieldPlainTextSection(organizationFields.name, schema?.[claimInformationFields.claim_facility_name], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldPlainTextSection(prescriberFields.locationPhone1, schema?.[prescriberFields.locationPhone1], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldPlainTextSection(prescriberFields.locationFaxNumber, schema?.[prescriberFields.locationFaxNumber], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      // fieldPlainTextSection(prescriberFields.locationAddressFull, schema?.[prescriberFields.locationAddressFull], {
      //   sizes: { md: 6 },
      //   condition: {
      //     when: 'payee_type',
      //     is: 'Practice'
      //   }
      // }),
      fieldPlainTextSection(prescriberFields.locationAddress1, schema?.[prescriberFields.locationAddress1], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldPlainTextSection(prescriberFields.locationAddress2, schema?.[prescriberFields.locationAddress2], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldPlainTextSection(prescriberFields.locationAddressCity, schema?.[prescriberFields.locationAddressCity], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldPlainTextSection(prescriberFields.locationAddressState, schema?.[prescriberFields.locationAddressState], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldPlainTextSection(prescriberFields.locationAddressZip, schema?.[prescriberFields.locationAddressZip], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3' }),

      headerSection(schema?.documents_header?.label, schema?.documents_header),
      spacerSection(2, { sizes: { sm: 12 }, className: 'mb-3 spacer-hr' }),
      plainTextSection(claimInformationFields.copayEOBHeader, schema?.[claimInformationFields.copayEOBHeader], {
        sizes: { md: 3 }
      }),
      fieldDownloadFileSection(
        claimInformationFields.copayClaimSupportDocumentUpload,
        schema?.[claimInformationFields.copayClaimSupportDocumentUpload],
        {
          sizes: { md: 12 }
        }
      )
    ].filter(Boolean)
  };
};
